import { useEffect } from "react";
import { Clock, Check, Pencil, Plus, Trash2 } from "lucide-react";
import { useRoadmap } from "@/context/RoadmapContext";

export default function HistoriqueView() {
  const { historyItems, loadHistory, canLoadMoreHistory } = useRoadmap();

  useEffect(() => {
    loadHistory(true);
  }, [loadHistory]);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
  };

  const getHistoryMeta = (item) => {
    if (item.status === "in-progress") {
      return {
        label: "En cours",
        container: "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.24)] border-[#bfd1c3] dark:border-[#2e6d3b]",
        badge: "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.24)] text-[#4c7e55] dark:text-[#2e6d3b]",
        icon: Clock,
        iconColor: "text-[#4c7e55] dark:text-[#2e6d3b]",
      };
    }

    if (item.status === "done") {
      return {
        label: "Terminée",
        container: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
        badge: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300",
        icon: Check,
        iconColor: "text-green-500",
      };
    }

    if (item.status === "cms-create") {
      return {
        label: "CMS · Création",
        container: "bg-[#dce9de] dark:bg-[rgba(46,109,59,0.3)] border-[#9fbaa5] dark:border-[#2e6d3b]",
        badge: "bg-[#e7f0e9] dark:bg-[rgba(46,109,59,0.22)] text-[#2f5d38] dark:text-[#5a9465]",
        icon: Plus,
        iconColor: "text-[#4c7e55] dark:text-[#2e6d3b]",
      };
    }

    if (item.status === "cms-delete") {
      return {
        label: "CMS · Suppression",
        container: "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.14)] border-[#bfd1c3] dark:border-[#355b3d]",
        badge: "bg-[#edf4ee] dark:bg-[rgba(46,109,59,0.18)] text-[#4c7e55] dark:text-[#4d8a5b]",
        icon: Trash2,
        iconColor: "text-[#4c7e55] dark:text-[#3d7548]",
      };
    }

    return {
      label: "CMS · Modification",
      container: "bg-[#e7f0e9] dark:bg-[rgba(46,109,59,0.2)] border-[#bfd1c3] dark:border-[#2e6d3b]",
      badge: "bg-[#edf4ee] dark:bg-[rgba(46,109,59,0.22)] text-[#3f6947] dark:text-[#4d8a5b]",
      icon: Pencil,
      iconColor: "text-[#4c7e55] dark:text-[#2e6d3b]",
    };
  };

  return (
    <div data-testid="historique-view">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight mb-2">
          Historique des activités
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
          Suivi chronologique des tâches et des modifications enregistrées via le CMS.
        </p>
      </div>

      {historyItems.length === 0 ? (
        <div className="py-16 text-center" data-testid="history-empty">
          <Clock className="mx-auto mb-4 w-12 h-12 text-gray-300 dark:text-gray-600" strokeWidth={1.5} />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Aucune activité pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {historyItems.map((item, i) => {
            const meta = getHistoryMeta(item);
            const Icon = meta.icon;

            return (
              <div
                key={`${item.task_id}-${item.changed_at}-${i}`}
                className={`flex flex-col sm:flex-row items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border ${meta.container} transition-colors`}
                data-testid="history-item"
              >
                <div className="flex items-start gap-3 sm:gap-4 flex-grow min-w-0 w-full">
                  <div className="shrink-0 mt-0.5">
                    <Icon className={`w-5 h-5 ${meta.iconColor}`} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white leading-snug">
                      {item.task_label}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${meta.badge}`}>{meta.label}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{item.tab}</span>
                      {item.user_first_name && <span className="text-xs text-gray-400 dark:text-gray-500" data-testid={`history-item-author-${i}`}>Par {item.user_first_name}</span>}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 text-left sm:text-right pl-8 sm:pl-0">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{formatDate(item.changed_at)}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">{formatTime(item.changed_at)}</div>
                </div>
              </div>
            );
          })}

          {canLoadMoreHistory && (
            <div className="text-center pt-4">
              <button
                onClick={() => loadHistory(false)}
                className="text-sm font-semibold text-[#4c7e55] dark:text-[#2e6d3b] hover:underline"
                data-testid="load-more-history-btn"
              >
                Charger plus…
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
